import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { AxiosError } from 'axios'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth-store'
import { ApiError, isAccountBlockedError } from '@/lib/api-client'
import { handleServerError } from '@/lib/handle-server-error'
import { AuthProvider } from './context/auth-provider'
import { DirectionProvider } from './context/direction-provider'
import { FontProvider } from './context/font-provider'
import { ThemeProvider } from './context/theme-provider'
// Generated Routes
import { routeTree } from './routeTree.gen'
// Styles
import './styles/index.css'

Sentry.init({
  dsn: 'https://6249b1878ece576303e16c9bfec23810@o4510963326058496.ingest.us.sentry.io/4510963328614400',
  sendDefaultPii: true,
})

function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof AxiosError) {
    return error.response?.status
  }

  if (error instanceof ApiError) {
    return error.status
  }

  if (error && typeof error === 'object' && 'status' in error) {
    const status = Number((error as { status?: unknown }).status)
    if (!Number.isNaN(status)) return status
  }

  return undefined
}

function shouldCaptureSentryError(error: unknown): boolean {
  const status = getErrorStatus(error)
  return !(status && [401, 403].includes(status))
}

function captureErrorToSentry(
  error: unknown,
  context: {
    source: 'query' | 'mutation'
    queryKey?: unknown
    mutationKey?: unknown
  }
) {
  if (!shouldCaptureSentryError(error)) return

  Sentry.withScope((scope) => {
    scope.setTag('error_source', context.source)

    const status = getErrorStatus(error)
    if (status) {
      scope.setTag('http_status', String(status))
    }

    if (context.queryKey) {
      scope.setContext('react_query', {
        queryKey: JSON.stringify(context.queryKey),
      })
    }

    if (context.mutationKey) {
      scope.setContext('react_query', {
        mutationKey: JSON.stringify(context.mutationKey),
      })
    }

    if (error instanceof Error) {
      scope.setTag('error_name', error.name)
    }

    Sentry.captureException(error)
  })
}

let sessionExpiryInFlight = false

function handleSessionExpired() {
  if (sessionExpiryInFlight) return
  sessionExpiryInFlight = true

  toast.error('Tu sesión ha expirado. Inicia sesión nuevamente.')
  useAuthStore.getState().reset()
  const redirect = `${router.history.location.href}`
  router.navigate({ to: '/sign-in', search: { redirect } })

  setTimeout(() => {
    sessionExpiryInFlight = false
  }, 1000)
}

// The account was blocked while it still had an open session.
function handleAccountBlocked(error: Error) {
  if (sessionExpiryInFlight) return
  sessionExpiryInFlight = true

  toast.error(error.message)
  useAuthStore.getState().reset()
  router.navigate({ to: '/sign-in' })

  setTimeout(() => {
    sessionExpiryInFlight = false
  }, 1000)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.log({ failureCount, error })

        if (failureCount >= 0 && import.meta.env.DEV) return false
        if (failureCount > 3 && import.meta.env.PROD) return false

        const status = getErrorStatus(error)
        return !(status && [401, 403].includes(status))
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000, // 10s
    },
    mutations: {
      onError: (error) => {
        captureErrorToSentry(error, { source: 'mutation' })

        const status = getErrorStatus(error)
        if (status === 401) {
          handleSessionExpired()
          return
        }

        if (isAccountBlockedError(error)) {
          handleAccountBlocked(error)
          return
        }

        handleServerError(error)

        if (status === 304) {
          toast.error('El contenido no fue modificado.')
        }
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      captureErrorToSentry(error, {
        source: 'query',
        queryKey: query.queryKey,
      })

      const status = getErrorStatus(error)

      if (status === 401) {
        handleSessionExpired()
        return
      }

      if (isAccountBlockedError(error)) {
        handleAccountBlocked(error)
        return
      }

      if (status === 500) {
        toast.error('Error interno del servidor.')
        // Only navigate to error page in production to avoid disrupting HMR in development
        if (import.meta.env.PROD) {
          router.navigate({ to: '/500' })
        }
      }

      if (status === 403) {
        // router.navigate("/forbidden", { replace: true });
      }
    },
  }),
})

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <FontProvider>
            <DirectionProvider>
              <AuthProvider>
                <RouterProvider router={router} />
              </AuthProvider>
            </DirectionProvider>
          </FontProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>
  )
}
